"use client";

import * as signalR from "@microsoft/signalr";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import { Link } from "@/i18n/navigation";
import { handleError } from "@/shared/api/error-handler";
import { api } from "@/shared/lib/api";
import { useVisibleInterval } from "@/shared/lib/hooks/use-visible-interval";
import { deepEqual } from "@/shared/lib/state-change-guard";
import { getHubBase } from "@/shared/lib/signalr-helpers";
import { getTeamOpggMultiSearch, getOpggUrl } from "@/shared/lib/opgg";
import { ProfilePreviewTrigger } from "@/features/profile-preview/ui/profile-preview-trigger";
import { TwitchStatusIcon } from "@/features/stream/twitch";
import { useTournamentStreams } from "@/entities/tournament/api/use-tournament-streams";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/data-display/avatar";
import { Badge } from "@/shared/ui/data-display/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { Button } from "@/shared/ui/buttons/button";
import { default as VerifiedBadge } from "@/shared/ui/data-display/verified-badge";
import { RankPill, MemberRoleSquare, CaptainStar } from "./team-card-bits";
import { formatDateOnlyMsk } from "@/shared/lib/msk-time";

interface TournamentTeamMember {
  userId: string;
  displayName: string;
  avatarUrl?: string;
  role: string;
  riotAccountName?: string;
  riotAccountTag?: string;
  riotAccountRegion?: string;
  riotAccounts?: Array<{
    gameName?: string | null;
    tagLine?: string | null;
    region?: string | null;
    isPrimary?: boolean | null;
  }>;
  rank?: string;
  joinedAt: string;
}

interface TournamentTeam {
  id: string;
  name: string;
  tag: string;
  logoUrl?: string;
  icon: string;
  captainId: string;
  captainName: string;
  captainAvatarUrl?: string;
  memberCount: number;
  maxMembers: number;
  members: TournamentTeamMember[];
  registeredAt: string;
  acceptScrimInvites?: boolean;
  isVerified?: boolean;
}

interface TournamentTeamsGridProps {
  tournamentId: string;
  hideSummary?: boolean;
}

export function TournamentTeamsGrid({
  tournamentId,
  hideSummary,
}: TournamentTeamsGridProps) {
  const t = useTranslations("Tournaments");
  const [teams, setTeams] = useState<TournamentTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const [pushConnected, setPushConnected] = useState(false);

  // Twitch-статусы игроков (KAN-93) — один батч-запрос на страницу турнира.
  const { data: streams } = useTournamentStreams(tournamentId);
  const streamByUserId = useMemo(
    () => new Map((streams?.playerStreams ?? []).map((s) => [s.userId, s])),
    [streams],
  );

  const fetchTeams = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!tournamentId) return;
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      if (!opts?.silent) setLoading(true);
      setError(null);
      try {
        const response = await api.get(`/tournaments/${tournamentId}/teams`);
        // 10-сек fallback-поллинг иначе ставил бы новую ссылку каждый тик и ререндерил
        // всю сетку команд, даже когда данные не менялись. Сохраняем прежнюю ссылку при
        // структурном равенстве (как preserveIfEqual в сторе турнира, KAN-236).
        setTeams((prev) =>
          deepEqual(prev, response.data) ? prev : response.data,
        );
      } catch (err) {
        setError(t("teamsGrid.loadError"));
        handleError(err, {
          component: "TournamentTeamsGrid",
          action: "fetchTeams",
          tournamentId,
        });
      } finally {
        isFetchingRef.current = false;
        setLoading(false);
      }
    },
    [tournamentId, t],
  );

  useEffect(() => {
    void fetchTeams();
  }, [fetchTeams]);

  // 1) Event-based refresh (from DraftRoom + other emitters)
  useEffect(() => {
    const onCompleted = (e: Event) => {
      try {
        const ce = e as CustomEvent<{ tournamentId?: string }>;
        if (ce?.detail?.tournamentId && ce.detail.tournamentId !== tournamentId)
          return;
      } catch {
        // Best-effort: некорректный detail в CustomEvent не должен блокировать обновление списка
      }
      void fetchTeams({ silent: true });
    };
    window.addEventListener("draft:completed", onCompleted as EventListener);
    return () =>
      window.removeEventListener(
        "draft:completed",
        onCompleted as EventListener,
      );
  }, [tournamentId, fetchTeams]);

  // 2) Страховочный опрос, пока пуш не поднялся.
  //
  // KAN-935: было 10 секунд — на анонимном зрителе DraftHub не встаёт вовсе
  // (хаб под [Authorize]), поэтому у него этот «fallback» работал всегда и был
  // постоянным опросом. Составы турнира меняются событием (`draft:completed`)
  // и в остальное время статичны, так что минуты хватает с запасом; плюс общий
  // гейт по видимости и догоняющий запрос при возврате на вкладку.
  useVisibleInterval(
    () => {
      void fetchTeams({ silent: true });
    },
    tournamentId && !pushConnected ? 60_000 : null,
  );

  // 3) Best-effort DraftHub push (fast path when authenticated)
  useEffect(() => {
    if (!tournamentId) return;

    const url = `${getHubBase()}/draft`;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(url, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    let alive = true;

    connection.onreconnecting(() => setPushConnected(false));
    // KAN-985: у переподключения новый ConnectionId, членства в группе нет
    // (KAN-824). Раньше здесь сразу ставилось pushConnected=true — это выключало
    // страховочный опрос, а пуш без повторного JoinDraftGroup молчал: сетка
    // застывала до перезагрузки. DraftCompleted к тому же мог пройти во время обрыва.
    connection.onreconnected(async () => {
      try {
        await connection.invoke("JoinDraftGroup", tournamentId);
        if (alive) setPushConnected(true);
      } catch {
        if (alive) setPushConnected(false);
      }
      if (alive) void fetchTeams({ silent: true });
    });
    connection.onclose(() => setPushConnected(false));

    const onDraftCompleted = () => void fetchTeams({ silent: true });
    connection.on("DraftCompleted", onDraftCompleted);
    (async () => {
      try {
        await connection.start();
        await connection.invoke("JoinDraftGroup", tournamentId);
        if (alive) setPushConnected(true);
      } catch {
        if (alive) setPushConnected(false);
      }
    })();

    return () => {
      alive = false;
      setPushConnected(false);
      connection.off("DraftCompleted", onDraftCompleted);
      (async () => {
        try {
          if (connection.state === signalR.HubConnectionState.Connected) {
            await connection.invoke("LeaveDraftGroup", tournamentId);
          }
        } catch {
          // Best-effort: соединение уже могло быть разорвано — важно всё равно вызвать stop() ниже
        }
        try {
          await connection.stop();
        } catch {
          // Best-effort: unmount-cleanup, компонент уже не отрендерен — ошибку показать некому
        }
      })();
    };
  }, [tournamentId, fetchTeams]);

  if (loading) {
    return <div className="text-center py-8">{t("teamsGrid.loading")}</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  if (teams.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t("teamsGrid.notFound")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {teams.map((team) => {
          const isSolo = team.maxMembers === 1;
          const soloMember = isSolo ? team.members?.[0] : undefined;

          const teamMulti = getTeamOpggMultiSearch(
            team.members.map((m) => ({
              gameName: m.riotAccountName,
              tagLine: m.riotAccountTag,
              region: m.riotAccountRegion,
              accounts: m.riotAccounts,
            })),
          );

          const opggUrl = isSolo
            ? soloMember?.riotAccountName && soloMember?.riotAccountTag
              ? getOpggUrl(
                  soloMember.riotAccountName,
                  soloMember.riotAccountTag,
                  soloMember.riotAccountRegion,
                )
              : null
            : (teamMulti?.url ?? null);

          if (isSolo && soloMember) {
            return (
              <Card
                key={team.id}
                className="relative overflow-hidden rounded-[14px] border border-border bg-card shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
              >
                <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(120%_180%_at_15%_0%,color-mix(in_srgb,var(--primary)_16%,transparent),transparent_70%)]" />
                <CardHeader className="relative pb-4">
                  <div className="flex items-center gap-4">
                    {/* FIX #35: avatar — открывает preview-модалку (как и было),
                        имя и Riot ID — теперь полноценные Link/a-теги, не button с
                        window.open. _blank ломается в Tauri webview → используем
                        native Link для профиля и `target="_blank" rel="noopener"` для OP.GG. */}
                    <ProfilePreviewTrigger userId={soloMember.userId}>
                      <Avatar className="h-14 w-14 border border-border/60 cursor-pointer">
                        <AvatarImage
                          src={soloMember.avatarUrl}
                          alt={soloMember.displayName}
                        />
                        <AvatarFallback className="text-xl font-bold">
                          {soloMember.displayName?.[0] ?? "?"}
                        </AvatarFallback>
                      </Avatar>
                    </ProfilePreviewTrigger>
                    <div className="min-w-0">
                      <Link
                        href={`/u/${soloMember.userId}`}
                        className="hover:underline"
                      >
                        <CardTitle className="text-lg font-bold leading-none truncate cursor-pointer hover:text-primary transition-colors">
                          {soloMember.displayName}
                        </CardTitle>
                      </Link>
                      {soloMember.riotAccountName &&
                        soloMember.riotAccountTag && (
                          <a
                            href={
                              getOpggUrl(
                                soloMember.riotAccountName,
                                soloMember.riotAccountTag,
                                soloMember.riotAccountRegion,
                              ) ?? "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 block w-full truncate text-left text-xs text-muted-foreground hover:text-primary hover:underline"
                          >
                            {soloMember.riotAccountName}#
                            {soloMember.riotAccountTag}
                          </a>
                        )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="relative space-y-3">
                  <div className="flex gap-2">
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs font-medium"
                    >
                      <Link href={`/u/${soloMember.userId}`}>
                        {t("teamsGrid.profile")}
                      </Link>
                    </Button>
                    {opggUrl && (
                      <Button
                        asChild
                        variant="secondary"
                        size="sm"
                        className="flex-1 text-xs font-medium"
                      >
                        <a
                          href={opggUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          OP.GG
                        </a>
                      </Button>
                    )}
                  </div>
                  <div className="text-center text-[10px] text-muted-foreground">
                    {t("teamsGrid.registered")}{" "}
                    {formatDateOnlyMsk(new Date(team.registeredAt))}
                  </div>
                </CardContent>
              </Card>
            );
          }

          return (
            <Card
              key={team.id}
              className="relative overflow-hidden rounded-[14px] border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-[radial-gradient(120%_180%_at_15%_0%,color-mix(in_srgb,var(--primary)_16%,transparent),transparent_70%)]" />
              <CardHeader className="relative pb-3">
                <Link
                  href={`/rosters/${team.id}`}
                  className="flex items-center gap-3 min-w-0 hover:opacity-90 transition-opacity"
                >
                  <Avatar className="h-12 w-12 shrink-0 rounded-lg ring-1 ring-primary/25">
                    <AvatarImage src={team.logoUrl} alt={team.name} />
                    <AvatarFallback className="rounded-lg bg-gradient-to-br from-primary/35 to-muted text-sm font-semibold">
                      {team.icon || team.tag}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base font-semibold leading-tight truncate flex items-center gap-1.5">
                      {team.name}
                      <VerifiedBadge isVerified={!!team.isVerified} size="sm" />
                    </CardTitle>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0"
                      >
                        {team.tag}
                      </Badge>
                      {team.acceptScrimInvites && (
                        <Badge
                          variant="secondary"
                          className="text-[10px] px-1.5 py-0"
                        >
                          SCRIMS
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {t("teamsGrid.slots", {
                          count: team.memberCount,
                          max: team.maxMembers,
                        })}
                      </span>
                    </div>
                  </div>
                </Link>
              </CardHeader>

              <CardContent className="relative space-y-3 pt-0">
                <div className="border-t border-border pt-3 mt-1">
                  <p className="text-xs text-muted-foreground mb-2">
                    {t("teamsGrid.roster")}
                  </p>
                  <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1 [scrollbar-width:thin] rounded-md">
                    {team.members.map((member) => (
                      <div
                        key={member.userId}
                        className="flex items-center gap-2.5 rounded-lg px-1.5 py-1.5 hover:bg-muted/40"
                      >
                        <MemberRoleSquare
                          role={member.role}
                          title={t("teamsGrid.roster")}
                        />
                        <ProfilePreviewTrigger userId={member.userId}>
                          <Avatar className="h-7 w-7 shrink-0 cursor-pointer rounded-md">
                            <AvatarImage
                              src={member.avatarUrl}
                              alt={member.displayName}
                            />
                            <AvatarFallback className="rounded-md text-[10px]">
                              {member.displayName?.[0] ?? "?"}
                            </AvatarFallback>
                          </Avatar>
                        </ProfilePreviewTrigger>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <Link
                              href={`/u/${member.userId}`}
                              className="truncate text-[13px] font-semibold hover:text-primary"
                            >
                              {member.displayName}
                            </Link>
                            {member.userId === team.captainId && (
                              <CaptainStar />
                            )}
                            <TwitchStatusIcon
                              stream={streamByUserId.get(member.userId)}
                            />
                          </div>
                          {member.riotAccountName && member.riotAccountTag && (
                            <a
                              href={
                                getOpggUrl(
                                  member.riotAccountName,
                                  member.riotAccountTag,
                                  member.riotAccountRegion,
                                ) ?? "#"
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block truncate text-[10px] tabular-nums text-muted-foreground hover:text-foreground"
                            >
                              {member.riotAccountName}#{member.riotAccountTag}
                            </a>
                          )}
                        </div>
                        <RankPill rank={member.rank} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 border-t pt-3">
                  {opggUrl && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full text-xs font-medium"
                      onClick={() => window.open(opggUrl, "_blank")}
                    >
                      Multisearch OP.GG
                    </Button>
                  )}
                  <div className="text-center text-[10px] text-muted-foreground">
                    {t("teamsGrid.registered")}{" "}
                    {formatDateOnlyMsk(new Date(team.registeredAt))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Summary — для 1v1 (SoloOneVsOne) показываем только «Игроков»,
          остальные ячейки бессмысленны (Команд == Игроков, Среднее = 1,
          Полных команд = Команд). Авто-определение по maxMembers=1. */}
      {!hideSummary &&
        (() => {
          const totalPlayers = teams.reduce(
            (sum, team) => sum + team.memberCount,
            0,
          );
          const isSolo =
            teams.length > 0 && teams.every((t) => t.maxMembers === 1);
          if (isSolo) {
            return (
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold">{totalPlayers}</div>
                    <div className="text-sm text-muted-foreground">
                      {t("teamsGrid.playersCount")}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          return (
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">{teams.length}</div>
                    <div className="text-sm text-muted-foreground">
                      {t("teamsGrid.teamsCount")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{totalPlayers}</div>
                    <div className="text-sm text-muted-foreground">
                      {t("teamsGrid.playersCount")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {Math.round(totalPlayers / teams.length || 0)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t("teamsGrid.avgTeamSize")}
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">
                      {
                        teams.filter(
                          (team) => team.memberCount >= team.maxMembers,
                        ).length
                      }
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t("teamsGrid.fullTeams")}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()}
    </div>
  );
}
